import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { COMPANY } from "@/lib/site";
import GoogleRatingBadge from "@/components/GoogleRatingBadge";

const STANDORTE: { slug: string; label: string }[] = [
  { slug: "bruchsal", label: "Bruchsal" },
  { slug: "bruehl", label: "Brühl" },
  { slug: "dielheim", label: "Dielheim" },
  { slug: "eppelheim", label: "Eppelheim" },
  { slug: "feudenheim", label: "Feudenheim" },
  { slug: "gaiberg", label: "Gaiberg" },
  { slug: "heidelberg", label: "Heidelberg" },
  { slug: "heilbronn", label: "Heilbronn" },
  { slug: "hockenheim", label: "Hockenheim" },
  { slug: "horrenberg", label: "Horrenberg" },
  { slug: "ilvesheim", label: "Ilvesheim" },
  { slug: "karlsruhe", label: "Karlsruhe" },
  { slug: "ketsch", label: "Ketsch" },
  { slug: "ladenburg", label: "Ladenburg" },
  { slug: "leimen", label: "Leimen" },
  { slug: "ludwigshafen", label: "Ludwigshafen" },
  { slug: "mannheim", label: "Mannheim" },
  { slug: "meckesheim", label: "Meckesheim" },
  { slug: "muehlhausen", label: "Mühlhausen" },
  { slug: "oftersheim", label: "Oftersheim" },
  { slug: "plankstadt", label: "Plankstadt" },
  { slug: "rauenberg", label: "Rauenberg" },
  { slug: "sandhausen", label: "Sandhausen" },
  { slug: "schwetzingen", label: "Schwetzingen" },
  { slug: "sinsheim", label: "Sinsheim" },
  { slug: "st-leon-rot", label: "St. Leon-Rot" },
  { slug: "waghaeusel", label: "Waghäusel" },
  { slug: "walldorf", label: "Walldorf" },
  { slug: "weinheim", label: "Weinheim" },
  { slug: "wiesloch", label: "Wiesloch" },
];

const Footer = () => (
  <footer className="bg-header border-t border-border mt-20">
    <div className="container-wm py-12 md:py-14 grid gap-x-8 gap-y-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-8">
      <div className="lg:col-span-2">
        <div className="flex items-center gap-3 mb-4 font-display">
          <img src="/images/wmee-logo.webp" alt="WM Erneuerbare Energien" loading="lazy" decoding="async" width={200} height={80} className="h-14 w-auto" />
          <span className="text-3xl font-extrabold tracking-tight text-foreground leading-none">WM</span>
          <span className="h-9 w-px bg-primary/70" />
          <span className="flex flex-col text-xs uppercase tracking-[0.22em] text-foreground font-semibold leading-tight">
            <span>erneuerbare</span>
            <span>Energien</span>
          </span>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Maßgeschneiderte Photovoltaik-Anlagen, Speicher, Wallboxen und Energiemanagement aus der Rhein-Neckar-Region.
        </p>
        <div className="flex items-center gap-3 mt-5">
          <a
            href="https://www.instagram.com/wm_erneuerbare_energien"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <Instagram size={20} />
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=61564469431714"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <Facebook size={20} />
          </a>
        </div>
        <div className="mt-5">
          <GoogleRatingBadge size="compact" />
        </div>
      </div>
      <div>
        <h3 className="text-sm uppercase tracking-[0.2em] text-primary mb-4">Kontakt</h3>
        <ul className="space-y-1 text-sm text-foreground">
          <li className="flex items-start gap-3 py-2"><MapPin size={16} className="mt-1 text-primary shrink-0" /><span>{COMPANY.name}<br />{COMPANY.street}<br />{COMPANY.city}</span></li>
          <li className="flex items-center gap-3"><Phone size={16} className="text-primary shrink-0" /><a href={`tel:${COMPANY.phone.replace(/\s|\(|\)/g,'')}`} className="inline-flex items-center min-h-[44px] hover:text-primary">{COMPANY.phone}</a></li>
          <li className="flex items-center gap-3"><Mail size={16} className="text-primary shrink-0" /><a href={`mailto:${COMPANY.email}`} className="inline-flex items-center min-h-[44px] hover:text-primary break-all">{COMPANY.email}</a></li>
        </ul>
      </div>
      <div className="sm:col-span-2 lg:col-span-2">
        <h3 className="text-sm uppercase tracking-[0.2em] text-primary mb-4">
          Aktiv in der gesamten Rhein-Neckar-Region
        </h3>
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
          {STANDORTE.map((s) => (
            <li key={s.slug}>
              <Link
                to={`/standorte/${s.slug}`}
                className="flex items-center min-h-[44px] px-3 -mx-3 rounded-lg text-sm text-foreground hover:bg-card hover:text-primary transition-colors"
              >
                {s.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="text-sm uppercase tracking-[0.2em] text-primary mb-4">Themen</h3>
        <ul className="text-sm space-y-1">
          <li><Link to="/leistungen/pv-fuer-einfamilienhaus" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">PV fürs Einfamilienhaus</Link></li>
          <li><Link to="/leistungen/solaranlage-mit-speicher-rhein-neckar" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Solaranlage mit Speicher</Link></li>
          <li><Link to="/solarteur-in-der-naehe" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Solarteur in deiner Nähe</Link></li>
          <li><Link to="/ratgeber/stromkosten-senken" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Stromkosten senken</Link></li>
          <li><Link to="/leistungen/stromspeicher-nachruesten" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Stromspeicher nachrüsten</Link></li>
          <li><Link to="/leistungen/wallbox-heidelberg" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Wallbox Heidelberg</Link></li>
        </ul>
      </div>
      <div>
        <h3 className="text-sm uppercase tracking-[0.2em] text-primary mb-4">Ratgeber</h3>
        <ul className="text-sm space-y-1">
          <li><Link to="/ratgeber/photovoltaik-foerderung-2026" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Photovoltaik Förderung 2026</Link></li>
          <li><Link to="/ratgeber/pv-anlage-erweitern" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">PV-Anlage erweitern</Link></li>
          <li><Link to="/ratgeber/pv-anlage-mit-waermepumpe" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">PV-Anlage mit Wärmepumpe</Link></li>
        </ul>
      </div>
      <div>
        <h3 className="text-sm uppercase tracking-[0.2em] text-primary mb-4">Rechtliches</h3>
        <ul className="text-sm space-y-1">
          <li><Link to="/impressum" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Impressum</Link></li>
          <li><Link to="/datenschutz" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Datenschutz</Link></li>
          <li><Link to="/agb" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">AGB</Link></li>
          <li><Link to="/cookie-policy" className="flex items-center min-h-[44px] -mx-2 px-2 rounded-md text-foreground hover:bg-card hover:text-primary transition-colors">Cookie-Richtlinie (EU)</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-border">
      <div className="container-wm py-5 text-xs text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
        <span>© {new Date().getFullYear()} {COMPANY.name}. Alle Rechte vorbehalten.</span>
        <span>Photovoltaik · Speicher · Wallbox · Energiemanagement</span>
      </div>
    </div>
  </footer>
);

export default Footer;
